import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  AlignedPageContainer,
  InsidePageContainer,
  WhitePageContainer,
} from '../common';
import {
  Button,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import translations from 'utils/translations';
import { useIntl } from 'react-intl';
import Folders from './Folders';
import PersonalExhibitorsList from './PersonalExhibitorsList';
import { LoaderContext } from 'context/LoaderContextProvider';
import { backHomeLink, isPostEvent } from 'utils/constants';
import { RedLinkButton } from 'pages/ticketV2/common';
import {
  VisitStatusEnum,
  folderActionsEnum,
  useBookmarkSelector,
  useBookmarkStore,
  useBookmarks,
} from 'pages/exhibitor/exhibitorBookmarks';
import {
  BookmarkDialog,
  CreateSection,
  RemoveFolderSection,
  RemoveFromFolderSection,
  RemoveSection,
} from '../common/Dialog';
import { useForm } from 'react-hook-form';
import { LoaderCountContext } from 'context/LoaderV5';
import { ExhibitorContext } from 'context/ExhibitorContextProvider';
import AddIcon from '@mui/icons-material/Add';
import MapOutlined from '@mui/icons-material/MapOutlined';
import { MapDialog } from '../common/Dialog';
import { setMapLayerEvent } from 'utils/dataLayerManager';

const dialogDefaultValues = {
  isOpen: false,
  title: '',
  children: undefined,
  onAgree: () => {},
  hasTextfield: false,
  confirmCustomText: '',
};
const DropDownListContainer = styled('div')({
  zIndex: '100',
  position: 'absolute',
  top: '110%',
  left: 0,
  '& > div::-webkit-scrollbar': {
    width: 6,
  },
  '& > div::-webkit-scrollbar-track': {
    background: '#ffffff',
  },
  '& > div::-webkit-scrollbar-thumb': {
    background: 'rgba(149, 152, 154, 0.43)',
    borderRadius: 5,
  },
  '& > div::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(149, 152, 154, 1)',
  },
});

const DropDownList = styled('div')({
  padding: 10,
  background: '#ffffff',
  boxShadow: '0 6px 12px rgb(0 0 0 / 18%)',
  maxHeight: '266px',
  overflowY: 'scroll',
  color: `black`,
  width: 225,
});
const ListItem = styled('div')(({ theme }) => ({
  listStyle: 'none',
  padding: ' 5px 15px',
  '&:hover': {
    color: `${theme.palette.primary.main}`,
    cursor: 'pointer',
  },
}));

const PersonalPage = ({ locale, loggedUser }) => {
  //hooks
  const intl = useIntl();
  const i18n = translations.ExhibitorsV2(intl).PersonalPage;
  // const i18nAlert = translations.ExhibitorsV2(intl).Alert;
  const i18nLegacy = translations.exhibitorBookmark(intl);
  const theme = useTheme();
  const history = useHistory();
  const is1024Up = useMediaQuery(theme.breakpoints.up(1024));
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { setLoaderActive } = useContext(LoaderCountContext);
  const { incognitoMode } = useContext(ExhibitorContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const [mapDialogState, setMapDialogState] = useState({
    isOpen: false,
    thematic: '',
  });
  const closeMapDialog = () =>
    setMapDialogState({
      isOpen: false,
      thematic: '',
    });
  const mapClickEvent = () => {
    setMapLayerEvent({
      event: 'popup_open',
      popup_name: 'salone_del_mobile_map',
    });
    setMapDialogState({ isOpen: true, thematic: '' });
  };
  const {
    folders: storeFolders,
    loggedUserId,
    addFolderToStore,
    isEditing,
    cardsToManage,
    removeFolderFromStore,
    renameFolderInStore,
    removeExhibitorFromStore,
    removeExhibitorsFromFolderInStore,
    moveExhibitorsToFolderInStore,
    currentFolderOnView,
  } = useBookmarkSelector();

  const {
    createFolder,
    deleteFolder,
    renameFolder,
    moveExhibitorsToFolder,
    saveExhibitors,
    deleteExhibitorsFromFolder,
  } = useBookmarks();
  const {
    isLoaderActive: isLegacyLoaderActive,
    setLoaderActive: setLegacyLoaderActive,
  } = useContext(LoaderContext);
  const hideLegacyLoader = () => setLegacyLoaderActive(false);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: undefined,
      prevName: undefined,
      folderId: undefined,
    },
  });
  const sameNameFolders = (name) => {
    const regex = /\(\d+\)/g;
    return (
      storeFolders?.filter((f) => f.name.replace(regex, '') === name)?.length ||
      0
    );
  };

  // States
  const [dialog, setDialog] = useState(dialogDefaultValues);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const toggling = () => setIsDropDownOpen((prev) => !prev);

  const closeAlert = (sev) =>
    useBookmarkStore.setState({ alertData: { severity: sev } });

  const showAlert = ({ title, message = '', sev = 'success' }) => {
    useBookmarkStore.setState({
      alertData: {
        title,
        message,
        onClose: () => closeAlert(sev),
        severity: sev,
      },
    });
  };

  useEffect(() => {
    if (incognitoMode) handleBack();
  }, [incognitoMode]);

  useEffect(() => {
    if (isLegacyLoaderActive) hideLegacyLoader();
  }, [isLegacyLoaderActive]);

  useEffect(() => {
    if (cardsToManage?.length) {
      useBookmarkStore.setState({ cardsToManage: [] });
    }
  }, [isEditing, currentFolderOnView]);

  const emptySelection = () =>
    useBookmarkStore.setState({
      cardsToManage: [],
    });

  useEffect(() => {
    if (storeFolders?.length === 0) {
      useBookmarkStore.setState({
        isEditing: false,
      });
    }
  }, [storeFolders]);

  // Functions
  const handleBack = () => {
    useBookmarkStore.setState({
      isEditing: false,
      cardsToManage: [],
      currentFolderOnView: null,
    });
    const params = new URLSearchParams(location.search);
    const personalPage = params.get('my_list');
    if (personalPage)
      window.location.href = `${backHomeLink}/${locale}/exhibitors`;
    const historyState = history.location.state;
    const state = {
      oldFilters: { ...historyState.oldFilters },
    };
    history.push('/', state);
  };

  // const closeAlert = (sev) =>
  //   useBookmarkStore.setState({ alertData: { severity: sev } });

  // const showAlert = ({ sev = 'success', title = '' }) =>
  //   useBookmarkStore.setState({
  //     alertData: {
  //       title: title,
  //       onClose: () => closeAlert(sev),
  //       severity: sev,
  //     },
  //   });

  const onMoveCardToFolder = async (folder) => {
    showLoader();
    const { folderId } = folder;
    const selectedExhibitors = cardsToManage.map((ex) => ex.salesforceId);
    const alreadyInFolders = {};
    selectedExhibitors.map((eId) => {
      const findFolder = storeFolders.find((folder) =>
        folder.exhibitors.includes(eId)
      );
      // È già in un altra folder, va cancellato da li
      if (findFolder) {
        alreadyInFolders[findFolder.folderId]
          ? (alreadyInFolders[findFolder.folderId] = [
              ...alreadyInFolders[findFolder.folderId],
              eId,
            ])
          : (alreadyInFolders[findFolder.folderId] = [eId]);
      }
    });
    const parsedAlreadyInFolder = Object.entries(alreadyInFolders).map(
      ([k, v]) => ({ folderId: k, exhibitorsIds: v })
    );
    const res = await moveExhibitorsToFolder({
      useLoader: false,
      salesforceId: loggedUserId,
      exhibitorsIds: selectedExhibitors,
      newFolderId: folderId,
      toBeRemoved: parsedAlreadyInFolder,
    });
    if (res) {
      moveExhibitorsToFolderInStore({
        folderId: folderId,
        exhibitorsIds: selectedExhibitors,
      });
    }
    emptySelection();
    setIsDropDownOpen(false);
    hideLoader();
    const isSingular = selectedExhibitors.length === 1;
    showAlert({
      title: `${selectedExhibitors.length} ${
        isSingular ? i18n.alerts.exhibitor : i18n.alerts.exhibitors
      } ${isSingular ? i18n.alerts.edit.sing : i18n.alerts.edit.plur} ${
        folder.name
      }`,
    });
  };

  const onRemove = () => {
    if (!cardsToManage?.length) return;
    if (currentFolderOnView?.folderId) {
      // Need to remove this exhibitors from folder
      generateRemoveFromFolderDialog();
    } else {
      // Need to remove Exhibitors from Visit/ToVisit Logic
      generateRemoveFromManageDialog();
    }
  };

  const handleRemoveFromManage = () => {
    cardsToManage
      .map((ex) => ex.salesforceId)
      .map((id) =>
        removeExhibitorFromStore({
          id: id,
        })
      );
    saveExhibitors({
      salesforceId: loggedUserId,
      status: VisitStatusEnum.NONE,
      exhibitorsIds: cardsToManage.map((ex) => ex.salesforceId),
    });
    setDialog(dialogDefaultValues);
    const isSingular = cardsToManage.length === 1;
    showAlert({
      title: `${cardsToManage.length} ${
        isSingular ? i18n.alerts.exhibitor : i18n.alerts.exhibitors
      } ${isSingular ? i18n.alerts.delete.sing : i18n.alerts.delete.plur}`,
    });
    emptySelection();
  };

  const handleRemoveFromFolder = async () => {
    if (!currentFolderOnView.folderId || !cardsToManage.length)
      return setDialog(dialogDefaultValues);
    try {
      showLoader();
      setDialog(dialogDefaultValues);
      await deleteExhibitorsFromFolder({
        useLoader: false,
        salesforceId: loggedUserId,
        folderId: currentFolderOnView.folderId,
        exhibitorsIds: cardsToManage.map((ex) => ex.salesforceId),
      });
      removeExhibitorsFromFolderInStore({
        folderId: currentFolderOnView.folderId,
        exhibitors: cardsToManage.map((ex) => ex.salesforceId),
      });
      const isSingular = cardsToManage.length === 1;
      showAlert({
        title: `${cardsToManage.length} ${
          isSingular ? i18n.alerts.exhibitor : i18n.alerts.exhibitors
        } ${isSingular ? i18n.alerts.remove.sing : i18n.alerts.remove.plur} ${
          currentFolderOnView.name
        }`,
      });
      emptySelection();
    } catch (error) {
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  const handleCreateFolder = async (data) => {
    setDialog(dialogDefaultValues);
    try {
      showLoader();
      const { name } = data;
      const parsedName =
        sameNameFolders(name) > 0 ? `${name}(${sameNameFolders(name)})` : name;
      const folderIdRes = await createFolder({
        useLoader: false,
        folderName: parsedName,
        salesforceId: loggedUserId,
      });
      if (folderIdRes) {
        addFolderToStore({
          folderId: folderIdRes,
          name: parsedName,
          exhibitors: [],
        });
      }
      if (cardsToManage.length) {
        const moveRes = await moveExhibitorsToFolder({
          useLoader: false,
          salesforceId: loggedUserId,
          exhibitorsIds: cardsToManage.map((ex) => ex.salesforceId),
          newFolderId: folderIdRes,
          toBeRemoved: [],
        });
        if (moveRes) {
          moveExhibitorsToFolderInStore({
            folderId: folderIdRes,
            exhibitorsIds: cardsToManage.map((ex) => ex.salesforceId),
          });
          const isSingular = cardsToManage.length === 1;
          showAlert({
            title: `${cardsToManage.length} ${
              isSingular ? i18n.alerts.exhibitor : i18n.alerts.exhibitors
            } ${
              isSingular ? i18n.alerts.edit.sing : i18n.alerts.edit.plur
            } ${parsedName}`,
          });
          emptySelection();
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  // renders
  const Header = () => (
    <>
      <Typography
        variant={'xxl-bold'}
        textAlign={isMdDown ? 'start' : 'center'}
      >
        {i18n.title}
      </Typography>
      <div>
        <Typography variant="body1" textAlign={isMdDown ? 'start' : 'center'}>
          {i18n.subtitle}
        </Typography>
      </div>
      <Stack
        flexDirection={isLgDown ? 'column' : 'row'}
        alignItems={'center'}
        justifyContent={'center'}
        gap={isLgDown ? '30px' : '40px'}
        marginTop={'20px'}
      >
        {/*   <Button
          variant="outlined"
          size="small"
          onClick={handleBack}
          startIcon={<AddIcon />}
          style={{
            ...(isMdDown && {
              width: '100%',
            }),
          }}
        >
          {i18n.backTxt}
        </Button> */}
        <Button
          variant="outlined"
          size="small"
          onClick={() => mapClickEvent()}
          startIcon={<MapOutlined />}
          style={{
            ...(isMdDown && {
              width: '100%',
            }),
          }}
        >
          {i18n.goToMap}
        </Button>
      </Stack>
    </>
  );

  const CreateNewFolderComponent = () => (
    <Stack
      flexDirection={is1024Up || isMdDown ? 'column' : 'row'}
      gap={is1024Up ? '40px' : '30px'}
      justifyContent={!is1024Up && !isMdDown ? 'space-between' : 'flex-start'}
    >
      <Typography variant={'body1'}>{i18n.emptyFoldersTitle}</Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={generateCreateFolderDialog}
      >
        {i18n.createNewFolder}
      </Button>
    </Stack>
  );

  const handleRemoveFolder = async (folderId) => {
    if (!folderId) return setDialog(dialogDefaultValues);
    try {
      showLoader();
      setDialog(dialogDefaultValues);
      await deleteFolder({
        useLoader: false,
        salesforceId: loggedUserId,
        folderId: folderId,
      });
      removeFolderFromStore({ folderId });
      if (currentFolderOnView?.folderId) {
        useBookmarkStore.setState({
          currentFolderOnView: null,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  const handleRenameFolder = async (data) => {
    if (!data.name || !data.folderId || data.name === data.prevName)
      return setDialog(dialogDefaultValues);
    try {
      const parsedName =
        sameNameFolders(data.name) > 0
          ? `${data.name}(${sameNameFolders(data.name)})`
          : data.name;
      showLoader();
      setDialog(dialogDefaultValues);
      await renameFolder({
        useLoader: false,
        salesforceId: loggedUserId,
        folderId: data.folderId,
        folderName: parsedName,
      });
      renameFolderInStore({ folderId: data.folderId, newName: parsedName });
      if (currentFolderOnView?.folderId) {
        useBookmarkStore.setState((prev) => ({
          currentFolderOnView: {
            ...prev.currentFolderOnView,
            folderId: data.folderId,
            name: parsedName,
          },
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  const onFolderCardAction = (action, folderId, folderName) => {
    const isDeleting = action === folderActionsEnum.DELETE;
    setDialog({
      isOpen: true,
      title:
        i18nLegacy.dialogs[isDeleting ? 'deleteFolder' : 'editFolder'].title,
      children: isDeleting ? (
        <RemoveFolderSection />
      ) : (
        <CreateSection
          control={control}
          isEdit={true}
          defaultValue={folderName}
          folderId={folderId}
        />
      ),
      onAgree: () =>
        isDeleting
          ? handleRemoveFolder(folderId)
          : handleSubmit(handleRenameFolder)(),
      confirmCustomText: '',
    });
  };

  const onDeleteFolderClick = (selectedFolder) =>
    onFolderCardAction(
      folderActionsEnum.DELETE,
      selectedFolder.folderId,
      selectedFolder.name
    );

  const onEditFolderClick = (selectedFolder) =>
    onFolderCardAction(
      folderActionsEnum.EDIT,
      selectedFolder.folderId,
      selectedFolder.name
    );

  const renderDialog = () => {
    if (!dialog.isOpen) return null;
    return (
      <BookmarkDialog
        isOpen={dialog.isOpen}
        onClose={() => setDialog(dialogDefaultValues)}
        i18n={i18nLegacy}
        onAgree={() => dialog.onAgree()}
        title={dialog.title}
        isSmDown={isSmDown}
        hasTextfield={dialog.hasTextfield}
        confirmCustomText={dialog.confirmCustomText || ''}
      >
        {dialog.children}
      </BookmarkDialog>
    );
  };

  const generateCreateFolderDialog = () => {
    if (isDropDownOpen) setIsDropDownOpen(false);
    setDialog({
      isOpen: true,
      title: i18nLegacy.dialogs.createFolder.title,
      children: <CreateSection control={control} />,
      onAgree: () => handleSubmit(handleCreateFolder)(),
      hasTextfield: true,
      confirmCustomText: '',
    });
  };

  const generateRemoveFromManageDialog = () =>
    setDialog({
      isOpen: true,
      title: isPostEvent
        ? i18nLegacy.dialogs.removePostEvent.title
        : i18nLegacy.dialogs.remove.title,
      children: <RemoveSection />,
      onAgree: handleRemoveFromManage,
      confirmCustomText: isPostEvent
        ? i18nLegacy.dialogs.removePostEvent.confirm
        : '',
    });

  const generateRemoveFromFolderDialog = () =>
    setDialog({
      isOpen: true,
      title: i18nLegacy.dialogs.removeFromFolder.title,
      children: <RemoveFromFolderSection />,
      onAgree: handleRemoveFromFolder,
      confirmCustomText: '',
    });

  return (
    <div>
      <InsidePageContainer>
        <WhitePageContainer>
          <Header />
        </WhitePageContainer>
      </InsidePageContainer>
      <AlignedPageContainer>
        <InsidePageContainer
          flexDirection={is1024Up ? 'row' : 'column'}
          gap={is1024Up ? '40px' : '30px'}
        >
          {!storeFolders?.length ? (
            <CreateNewFolderComponent />
          ) : (
            <Folders
              onCreateNewFolder={generateCreateFolderDialog}
              onDeleteFolderClick={onDeleteFolderClick}
              onEditFolderClick={onEditFolderClick}
            />
          )}
          <Stack width={'100%'} gap={'25px'}>
            <Stack
              flexDirection={isMdDown ? 'column-reverse' : 'row'}
              justifyContent={isEditing ? 'space-between' : 'flex-end'}
              minHeight={isMdDown ? 'unset' : '44px'}
              gap={isMdDown ? '30px' : 0}
            >
              {isEditing && (
                <Stack
                  flexDirection={'row'}
                  gap={'25px'}
                  alignItems={'center'}
                  justifyContent={isSmDown ? 'space-between' : 'flex-start'}
                >
                  <div style={{ position: 'relative' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!cardsToManage.length || !storeFolders?.length}
                      onClick={toggling}
                    >
                      {i18n.moveExhibitor}
                    </Button>
                    {isDropDownOpen && (
                      <DropDownListContainer>
                        <DropDownList>
                          <>
                            {storeFolders
                              ? storeFolders
                                  ?.filter((option) => {
                                    if (currentFolderOnView?.folderId)
                                      return (
                                        option.folderId !==
                                        currentFolderOnView.folderId
                                      );
                                    else return option;
                                  })
                                  .map((option, idx) => (
                                    <ListItem
                                      onClick={() => onMoveCardToFolder(option)}
                                      key={idx}
                                    >
                                      <Typography margin={0} variant={'body1'}>
                                        {option.name}
                                      </Typography>
                                    </ListItem>
                                  ))
                              : null}
                            <ListItem
                              onClick={() => generateCreateFolderDialog()}
                            >
                              <Typography
                                margin={0}
                                variant={'body1'}
                                color={theme.palette.primary.main}
                                sx={{
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                }}
                              >
                                {i18n.createNewFolder}
                              </Typography>
                            </ListItem>
                          </>
                        </DropDownList>
                      </DropDownListContainer>
                    )}
                  </div>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!cardsToManage.length}
                    onClick={() => onRemove()}
                  >
                    {i18n.removeExhibitor}
                  </Button>
                </Stack>
              )}
              <RedLinkButton
                msg={isEditing ? i18n.exitEditMode : i18n.manageExhibitors}
                onClick={() =>
                  useBookmarkStore.setState((prev) => ({
                    isEditing: !prev.isEditing,
                  }))
                }
              />
            </Stack>
            <PersonalExhibitorsList locale={locale} loggedUser={loggedUser} />
          </Stack>
          {renderDialog()}
        </InsidePageContainer>
      </AlignedPageContainer>
      <MapDialog
        isOpen={mapDialogState.isOpen}
        onClose={() => closeMapDialog()}
        locale={locale}
        token={loggedUser ? '&token=' + loggedUser.tk : ''}
      />
    </div>
  );
};
export default PersonalPage;
